import type { GpuContext } from './context.js';
import type { GpuImage } from './resources.js';
import { SHADER_SOURCES } from './shaders/index.js';
import { Struct } from './uniform-writer.js';

export interface ViewTransform {
  /** Zoom factor; 1 fits the artwork to the canvas. */
  zoom: number;
  /** Pan in normalised artwork units. */
  panX: number;
  panY: number;
}

export interface PresentOptions {
  /** Draw the alpha channel as greyscale instead of the composite. */
  alphaOnly?: boolean;
  premultiplied?: boolean;
  checkerSize?: number;
}

/**
 * Draws the working image to a canvas through a fullscreen triangle.
 *
 * A render pass rather than `copyTextureToTexture`, because the canvas format is
 * whatever `getPreferredCanvasFormat()` returns - typically `bgra8unorm`, which
 * is not a core storage format - and because zooming wants real filtering rather
 * than the nearest-neighbour a copy would give.
 */
export class CanvasPresenter {
  readonly #context: GPUCanvasContext;
  readonly #pipeline: GPURenderPipeline;
  readonly #layout: GPUBindGroupLayout;
  readonly #sampler: GPUSampler;
  readonly #format: GPUTextureFormat;

  constructor(
    private readonly ctx: GpuContext,
    readonly canvas: HTMLCanvasElement | OffscreenCanvas,
  ) {
    const context = canvas.getContext('webgpu') as GPUCanvasContext | null;
    if (!context) throw new Error('[gpu-core] canvas does not support a webgpu context');
    this.#context = context;
    this.#format = navigator.gpu.getPreferredCanvasFormat();
    context.configure({
      device: ctx.device,
      format: this.#format,
      alphaMode: 'opaque',
    });

    this.#sampler = ctx.device.createSampler({
      label: 'display',
      magFilter: 'linear',
      minFilter: 'linear',
      addressModeU: 'clamp-to-edge',
      addressModeV: 'clamp-to-edge',
    });

    this.#layout = ctx.device.createBindGroupLayout({
      label: 'display',
      entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      ],
    });

    const module = ctx.device.createShaderModule({
      label: 'display.wgsl',
      code: SHADER_SOURCES['display.wgsl']!,
    });
    this.#pipeline = ctx.device.createRenderPipeline({
      label: 'display',
      layout: ctx.device.createPipelineLayout({ bindGroupLayouts: [this.#layout] }),
      vertex: { module, entryPoint: 'vs' },
      fragment: { module, entryPoint: 'fs', targets: [{ format: this.#format }] },
      primitive: { topology: 'triangle-list' },
    });
  }

  /** Resizes the backing store to the CSS size at the given device pixel ratio. */
  resize(cssWidth: number, cssHeight: number, dpr = globalThis.devicePixelRatio || 1): void {
    const width = Math.max(1, Math.floor(cssWidth * dpr));
    const height = Math.max(1, Math.floor(cssHeight * dpr));
    if (this.canvas.width !== width) this.canvas.width = width;
    if (this.canvas.height !== height) this.canvas.height = height;
  }

  /**
   * Draws `image`, letterboxed to preserve its aspect ratio.
   *
   * The UV transform maps the canvas onto the artwork, so zoom and pan cost
   * nothing beyond two vec2s.
   */
  present(image: GpuImage, view: ViewTransform, options: PresentOptions = {}): void {
    const canvasWidth = this.canvas.width;
    const canvasHeight = this.canvas.height;
    const zoom = Math.max(0.01, view.zoom);

    // uv span the viewport covers, in artwork units.
    const canvasAspect = canvasWidth / canvasHeight;
    const imageAspect = image.width / image.height;
    let spanX = 1 / zoom;
    let spanY = 1 / zoom;
    if (canvasAspect > imageAspect) {
      spanX = (canvasAspect / imageAspect) / zoom;
    } else {
      spanY = (imageAspect / canvasAspect) / zoom;
    }

    const params = new Struct(48)
      .f(0, spanX, spanY)
      .f(8, (1 - spanX) * 0.5 + view.panX, (1 - spanY) * 0.5 + view.panY)
      .f(16, (options.checkerSize ?? 12) * (globalThis.devicePixelRatio || 1), 0.93, 0.82, 0)
      .u(32, options.premultiplied ? 1 : 0, options.alphaOnly ? 1 : 0, 0, 0);

    const bindGroup = this.ctx.device.createBindGroup({
      label: 'display',
      layout: this.#layout,
      entries: [
        { binding: 0, resource: image.view },
        { binding: 1, resource: this.#sampler },
        { binding: 2, resource: this.ctx.uniform(params.bytes) },
      ],
    });

    this.ctx.endPass();
    const encoder = this.ctx.encoder();
    const pass = encoder.beginRenderPass({
      label: 'present',
      colorAttachments: [
        {
          view: this.#context.getCurrentTexture().createView(),
          loadOp: 'clear',
          storeOp: 'store',
          clearValue: { r: 0.09, g: 0.09, b: 0.11, a: 1 },
        },
      ],
    });
    pass.setPipeline(this.#pipeline);
    pass.setBindGroup(0, bindGroup);
    pass.draw(3);
    pass.end();
    this.ctx.flush();
  }

  /** Maps a canvas-space point to artwork pixel coordinates. */
  canvasToImage(
    image: GpuImage,
    view: ViewTransform,
    canvasX: number,
    canvasY: number,
  ): { x: number; y: number } {
    const zoom = Math.max(0.01, view.zoom);
    const canvasAspect = this.canvas.width / this.canvas.height;
    const imageAspect = image.width / image.height;
    let spanX = 1 / zoom;
    let spanY = 1 / zoom;
    if (canvasAspect > imageAspect) {
      spanX = (canvasAspect / imageAspect) / zoom;
    } else {
      spanY = (imageAspect / canvasAspect) / zoom;
    }
    const u = (canvasX / this.canvas.width) * spanX + (1 - spanX) * 0.5 + view.panX;
    const v = (canvasY / this.canvas.height) * spanY + (1 - spanY) * 0.5 + view.panY;
    return { x: u * image.width, y: v * image.height };
  }
}
